/**
 * Reports Index Page
 * 
 * Lists all available admin reports
 * - Active Riders Report (Email List)
 * - Coaches Report
 * - Riders Report
 * - Skills Report
 *
 * Access: admin-only. The route `/admin/reports` is wrapped in
 * `<ProtectedRoute requireAdmin>` in App.tsx, so every card below —
 * including the Incidents Report — is only reachable by admins.
 * No per-card role guard is needed.
 */

import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/common/Card';
import { Button } from '../../components/common/Button';

export default function Reports() {
  const navigate = useNavigate();

  const handleNavigate = useCallback((path: string) => {
    navigate(path);
  }, [navigate]);

  const reports = [
    {
      title: 'Active Riders Report',
      subtitle: 'Email List',
      description: 'View and export email list of all active riders and their associated users',
      icon: '📧',
      path: '/admin/reports/active-riders',
      color: 'bg-blue-500',
    },
    {
      title: 'Coaches Report',
      subtitle: 'Statistics & Details',
      description: 'View coaches with attendance statistics, grouped by level and group',
      icon: '👨‍🏫',
      path: '/admin/reports/coaches',
      color: 'bg-green-500',
    },
    {
      title: 'Riders Report',
      subtitle: 'Statistics & Details',
      description: 'View riders with attendance statistics, grouped by category and group',
      icon: '🚴',
      path: '/admin/reports/riders',
      color: 'bg-purple-500',
    },
    {
      title: 'Skills Report',
      subtitle: 'Rankings & Scores',
      description: 'View riders ranked by skills score within each group',
      icon: '🎯',
      path: '/admin/reports/skills',
      color: 'bg-orange-500',
    },
    {
      title: 'Group Membership Report',
      subtitle: 'Group Assignments',
      description: 'View current group memberships with rider counts and filtering by group and type',
      icon: '👥',
      path: '/admin/reports/group-membership',
      color: 'bg-teal-500',
    },
    {
      title: 'Race Registration Report',
      subtitle: 'Per-Event Registration',
      description: 'View and export per-event race registration status for all riders',
      icon: '🏁',
      path: '/admin/reports/race-registration',
      color: 'bg-red-500',
    },
    {
      title: 'Incidents Report',
      subtitle: 'Practice Safety',
      description: 'View and filter practice incidents across all events',
      icon: '⚠️',
      path: '/admin/reports/incidents',
      color: 'bg-orange-500',
    },
  ];

  return (
    <div className="min-h-screen bg-primary-50">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="container mx-auto px-4 py-4">
          <h1 className="text-3xl font-bold text-primary-900">Reports</h1>
          <p className="text-primary-600 mt-1">
            View and generate reports for events, riders, and attendance
          </p>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 pt-3 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {reports.map((report) => (
            <Card key={report.path} className="hover:shadow-lg transition-shadow">
              <div className="p-6">
                {/* Icon */}
                <div className={`w-16 h-16 ${report.color} rounded-lg flex items-center justify-center text-3xl mb-4`}>
                  {report.icon}
                </div>

                {/* Title */}
                <h2 className="text-xl font-semibold text-primary-900 mb-1">
                  {report.title}
                </h2>
                <p className="text-sm text-primary-600 font-medium mb-3">
                  {report.subtitle}
                </p>

                {/* Description */}
                <p className="text-base text-gray-600 mb-6">
                  {report.description}
                </p>

                {/* View Button */}
                <Button
                  onClick={() => handleNavigate(report.path)}
                  className="w-full"
                >
                  View Report
                </Button>
              </div>
            </Card>
          ))}
        </div>
      </main>
    </div>
  );
}
